import React, { Component } from 'react';
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { Loader, Table } from 'semantic-ui-react';
import Requests from '../../../services/requests';
import '../map.scss';
import { Utils } from '../../../services/utils/utils';

// Map of Data Types and their units key for translation
const dataTypeUnits = {
    "CONCENTRATION": "kg_m3",
    "INDOOR_CONCENTRATION": "kg_m3",
    "INDOOR_DOSAGE": "kgs_m3",
    "DOSAGE": "kgs_m3",
    "LCT50": false,
    "ICT50": false,
    "ECT50": false,
    "AEGLS": false,
    "DEPOSITION": "kg_m2",
    "BLAST": "psi",
    "ATP-45": false,    
    "CED_WORKER_SLOW_LUNG_CLASS": "msv",
    "CED_WORKER_MED_LUNG_CLASS": "msv",
    "CED_WORKER_FAST_LUNG_CLASS": "msv",
    "CED_WORKER_VAPOUR": "msv",
    "CED_WORKER_INERT": "msv",
    "CED_ADULT_SLOW_LUNG_CLASS": "msv",
    "CED_ADULT_MED_LUNG_CLASS": "msv",
    "CED_ADULT_FAST_LUNG_CLASS": "msv",
    "CED_ADULT_INERT": "msv",
    "CED_15_YEAR_OLD_SLOW_LUNG_CLASS": "msv",
    "CED_15_YEAR_OLD_MED_LUNG_CLASS": "msv",
    "CED_15_YEAR_OLD_FAST_LUNG_CLASS": "msv",
    "CED_15_YEAR_OLD_INERT": "msv",
    "CED_10_YEAR_OLD_SLOW_LUNG_CLASS": "msv",
    "CED_10_YEAR_OLD_MED_LUNG_CLASS": "msv",
    "CED_10_YEAR_OLD_FAST_LUNG_CLASS": "msv",
    "CED_10_YEAR_OLD_INERT": "msv",
    "CED_5_YEAR_OLD_SLOW_LUNG_CLASS": "msv",
    "CED_5_YEAR_OLD_MED_LUNG_CLASS": "msv",
    "CED_5_YEAR_OLD_FAST_LUNG_CLASS": "msv",
    "CED_5_YEAR_OLD_INERT": "msv",
    "CED_1_YEAR_OLD_SLOW_LUNG_CLASS": "msv",
    "CED_1_YEAR_OLD_MED_LUNG_CLASS": "msv",
    "CED_1_YEAR_OLD_FAST_LUNG_CLASS": "msv",
    "CED_1_YEAR_OLD_INERT": "msv",
    "CED_INFANT_SLOW_LUNG_CLASS": "msv",
    "CED_INFANT_MED_LUNG_CLASS": "msv",
    "CED_INFANT_FAST_LUNG_CLASS": "msv",
    "CED_INFANT_INERT": "msv"
}

class OutputPopupContent extends Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            error: false,
            queryParams: props.queryParams,
            outputData: []
        }
    }

    componentDidMount() {
        const url = '/output/scenario/' + this.props.simulationState.selectedSimulation.scenarioId + '?';
        const queryParams = this.state.queryParams;

        Requests.get(url + new URLSearchParams(queryParams))
            .then(contJson => {
                if (contJson) {
                    this.setState({ outputData: contJson, loading: false, error: false });
                } else {
                    console.error("failed to get contour data");
                    this.setState({ loading: false, error: true });
                }
            })
            .catch(err => {
                console.error("failed to get contour data", err);
                this.setState({ loading: false, error: true });
            })
    }

    shouldComponentUpdate(prevProps, prevState) {
        return prevState.loading !== this.state.loading ||
            prevState.outputData.length !== this.state.outputData.length;
    }

    getOutputView = () => {
        const { t } = this.props;

        if (this.state.loading) {
            return (
                <Loader active inline='centered'>{t("keyBuild.loadingData")}...</Loader>
            )
        }

        if (this.state.error) {
            return (
                t('messages.errorCheckLogs')
            )
        }

        if (this.state.outputData.length === 0) {
            return null;
        }

        const firstOutput = this.state.outputData[0];

        // All the output data will be for the same data type/material, get the first data type and material to use in title
        const dataTypeTitle = t('dataTypes.' + firstOutput.dataType);
        const materialTitle = firstOutput.material ? firstOutput.material : "";
        const dateTime = new Date(firstOutput.time).toLocaleString();
        let hasUnits = dataTypeUnits[firstOutput.dataType];
        let title = dataTypeTitle;
        if (hasUnits) {
            title += " (" + t('units.' + hasUnits) + ")";
        }

        return (
            <>
                <div style={{ "textAlign": "center" }}>
                    <div style={{ "fontWeight": "800", "fontSize": "16px" }}>{title}</div>
                    <div style={{ "fontWeight": "600", "fontSize": "14px" }}>{materialTitle}</div>
                    <div>{dateTime}</div>
                </div>
                <div style={{ "marginTop": "10px" }}>
                    <div className="chart-div">
                        {this.getTable()}
                    </div>
                </div>
                <div style={{ "textAlign": "center" }}>
                    {t("contourPopup.messages.appliesToSameMaterialsAndTypes")}
                </div>
            </>
        );
    }

    getTable = () => {
        const { t } = this.props;
        const tableBodyRows = [];

        let hasUnits = dataTypeUnits[this.state.outputData[0].dataType];

        for (let ind in this.state.outputData) {
            const output = this.state.outputData[ind];
            let population = t('contourPopup.messages.noData');
            if (output.population && output.population.population) {
                population = Math.floor(output.population.population);
            }
            tableBodyRows.push(<Table.Row key={"output-row-" + ind}>
                <Table.Cell><i style={{ background: output.colour, height: '18px', display: 'block' }} /></Table.Cell>
                <Table.Cell>{output.label}</Table.Cell>
                {!Utils.OFFLINE_MAPS ? <Table.Cell>{population}</Table.Cell> : null}
                <Table.Cell>{output.area}</Table.Cell>
            </Table.Row>)
        }

        return (
            <Table basic>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>{t('contourPopup.labels.key')}</Table.HeaderCell>
                        <Table.HeaderCell>{t('contourPopup.labels.label')}</Table.HeaderCell>
                        {!Utils.OFFLINE_MAPS ? <Table.HeaderCell>{t('label.population')}</Table.HeaderCell> : null}
                        <Table.HeaderCell>{t('contourPopup.labels.area')}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {tableBodyRows}
                </Table.Body>
            </Table>
        )
    }

    render() {
        return this.getOutputView();   
    }

}

const mapStateToProps = (store) => {
    return {
        simulationState: store.simulationState,
        scenarioState: store.scenarioState,
        currentTime: store.mapState.currentTime,
        position: store.mapState.position,
    }
}
export default compose(withTranslation(), connect(mapStateToProps))(OutputPopupContent);